import * as React from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { cn } from '@/lib/utils';
import { api } from '@/lib/api/client';
import { Button } from '@/components/ui/button';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command';

interface StartableTask {
  id: number;
  description: string | null;
  newStatus: string;
}

interface StartableTaskSelectProps {
  value?: number | undefined;
  onChange: (id: number | undefined) => void;
  placeholder?: string | undefined;
  disabled?: boolean | undefined;
}

/**
 * Task picker for starting a time-tracking activity. Lists ONLY the tasks the
 * current user can start/stop on — `GET /tasks/startable` (same set as the My
 * Tasks board, resolved per matter role on the backend). The endpoint is **not
 * paginated**: it returns the full list in one shot, and this component filters
 * client-side (Command's built-in filter). The selected task is always a fresh
 * pick (the parent dialogs only render this in create mode).
 */
export function StartableTaskSelect({
  value,
  onChange,
  placeholder,
  disabled = false,
}: StartableTaskSelectProps) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);

  const { data, isFetching } = useQuery({
    queryKey: ['tasks', 'startable'],
    queryFn: () => api.get<{ results: StartableTask[] }>('/tasks/startable'),
    enabled: open,
    staleTime: 60 * 1000,
  });

  const tasks = data?.results ?? [];
  const selected = tasks.find((tk) => tk.id === value) ?? null;
  const label = (tk: StartableTask) => tk.description ?? `#${tk.id}`;

  return (
    <Popover open={!disabled && open} onOpenChange={(s) => !disabled && setOpen(s)}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full min-w-0 justify-between overflow-hidden"
        >
          <span className="min-w-0 flex-1 truncate text-left">
            {selected ? label(selected) : (placeholder ?? t('selectTask'))}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-[280px] p-0">
        <Command label={t('search') ?? 'Search'}>
          <CommandInput placeholder={t('search') ?? 'Search...'} className="h-9" />
          <CommandList>
            <CommandEmpty>
              {isFetching ? (t('loading') ?? 'Loading...') : (t('noDataFound') ?? 'No results found')}
            </CommandEmpty>
            <CommandGroup>
              {tasks.map((tk) => (
                <CommandItem
                  key={tk.id}
                  value={`${tk.description ?? ''} ${tk.id}`}
                  onSelect={() => {
                    onChange(value === tk.id ? undefined : tk.id);
                    setOpen(false);
                  }}
                >
                  <span className="min-w-0 flex-1 truncate">{label(tk)}</span>
                  <Check
                    className={cn(
                      'ml-2 h-4 w-4 shrink-0',
                      value === tk.id ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
